import {
  CallMdIcon,
  CallSmIcon,
  EmailMdIcon,
  EmailSmIcon,
  LinkedinInMailMessageMdIcon,
  LinkedinInMailMessageSmIcon,
  LinkedinInvitationMdIcon,
  LinkedinInvitationSmIcon,
  LinkedinMessageMdIcon,
  LinkedinMessageSmIcon,
  LinkedinVoiceNoteMdIcon,
  LinkedinVoiceNoteSmIcon,
  TodoMdIcon,
  TodoSmIcon,
} from '@crono-one/design-system';
import { useAuth } from 'context/auth';
import { TaskTodoSubtype } from 'crono-fe-common/types/enums/taskTodoSubtype';
import { TaskTodoType } from 'crono-fe-common/types/enums/taskTodoType';
import { ComponentType } from 'react';
import { createHashedObject } from 'utils/uiHelpers';
import { TaskOption } from '.';

export interface TaskTypeOption {
  type: TaskTodoType;
  subtype?: TaskTodoSubtype;
  IconSm: ComponentType<{ className?: string }>;
  IconMd: ComponentType<{ className?: string }>;
  iconWrapperClass: string;
  text: string;
  title: string;
  longTitle?: string;
  hidden?: boolean;
  hideToolbar?: boolean;
  templateHidden?: boolean;
  automateHidden?: boolean;
  forceTimePickerVisible?: boolean;
}

export const useTaskTypeOptions = () => {
  const { hasSalesNav, showUpgradeBadges } = useAuth();

  const typeOptions: TaskTypeOption[] = [
    {
      type: TaskTodoType.CALL,
      IconSm: CallSmIcon,
      IconMd: CallMdIcon,
      text: 'Change to Call',
      title: 'Call',
      hidden: showUpgradeBadges,
      iconWrapperClass: 'bg-green-500',
      automateHidden: true,
    },
    {
      type: TaskTodoType.EMAIL,
      IconSm: EmailSmIcon,
      IconMd: EmailMdIcon,
      text: 'Change to Email',
      title: 'Email',
      hidden: showUpgradeBadges,
      iconWrapperClass: 'bg-red-500',
    },
    {
      type: TaskTodoType.LINKEDIN,
      subtype: TaskTodoSubtype.LinkedinInvitation,
      text: 'Change to Linkedin Invitation',
      IconSm: LinkedinInvitationSmIcon,
      IconMd: LinkedinInvitationMdIcon,
      title: 'Invitation',
      longTitle: 'LinkedIn Invitation',
      iconWrapperClass: 'bg-blue-600',
    },
    {
      type: TaskTodoType.LINKEDIN,
      subtype: TaskTodoSubtype.LinkedinMessage,
      text: 'Change to Linkedin Message',
      IconSm: LinkedinMessageSmIcon,
      IconMd: LinkedinMessageMdIcon,
      title: 'Message',
      longTitle: 'LinkedIn Message',
      iconWrapperClass: 'bg-blue-600',
    },
    {
      type: TaskTodoType.LINKEDIN,
      subtype: TaskTodoSubtype.LinkedinVoiceNote,
      text: 'Change to Linkedin Voice Note',
      IconSm: LinkedinVoiceNoteSmIcon,
      IconMd: LinkedinVoiceNoteMdIcon,
      title: 'Voice Note',
      iconWrapperClass: 'bg-blue-600',
      longTitle: 'LinkedIn Voice Note',
    },
    {
      type: TaskTodoType.IN_MAIL,
      IconSm: LinkedinInMailMessageSmIcon,
      IconMd: LinkedinInMailMessageMdIcon,
      text: 'Change to Linkedin InMail',
      hidden: !hasSalesNav,
      title: 'InMail',
      iconWrapperClass: 'bg-purple-600',
      longTitle: 'LinkedIn InMail',
    },
    {
      type: TaskTodoType.GENERIC,
      IconSm: TodoSmIcon,
      IconMd: TodoMdIcon,
      text: 'Change to To-do',
      hideToolbar: true,
      templateHidden: true,
      automateHidden: true,
      forceTimePickerVisible: true,
      title: 'To-do',
      iconWrapperClass: 'bg-pink-500',
    },
  ];

  function getVisibleTaskTypeOptions(currentTask?: TaskOption) {
    return typeOptions.filter((option) => {
      // Show if not hidden
      if (option.hidden) {
        return false;
      }

      if (!currentTask) {
        return true;
      }

      // Show if option is not selected
      // An option is selected if type matches AND subtypes match (or both are absent)
      const isSelected =
        option.type === currentTask.type &&
        (option.subtype ?? null) === (currentTask.subtype ?? null);

      return !isSelected;
    });
  }

  function getTaskTypeOptionConfig(
    type: TaskTodoType,
    subtype?: TaskTodoSubtype,
  ) {
    const hashedTypeOptionsBySubType = createHashedObject(
      typeOptions,
      'subtype',
    );
    const hashedTypOptionsByType = createHashedObject(typeOptions, 'type');

    if (subtype) {
      return hashedTypeOptionsBySubType[subtype];
    }

    return hashedTypOptionsByType[type];
  }

  return {
    typeOptions,
    getVisibleTaskTypeOptions,
    getTaskTypeOptionConfig,
  };
};
